import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import _isEqual from 'lodash/isEqual';

import ReactTable from './ReactTable';
import {
  dateFilterDataFn,
  dropdownFilterDataFn,
  numberFilterDataFn,
  textFilterDataFn
} from './ReactTableFilters';
import { downloadExcelByJsonFn } from './ExportExcel';
import { DESCENDING } from '../../../constants/messageConst';
import CustomCard from '../CustomCard';

const ReactTableContainer = props => {
  const { isLoaded, gridOption } = props;

  const gridRowData = gridOption?.rowData || [];
  const columnDefs = gridOption?.columnDefs;
  const isDownloadXls = gridOption?.isDownloadXls;
  const isPagination = gridOption?.isPagination;
  const gridOptionId = gridOption?.id || '';
  const hideHeader = gridOption?.hideHeader || false;
  const isShowRowLoadingIcon = gridOption?.isShowRowLoadingIcon || false;
  const cardClassName = props?.cardClassName || '';
  const sortingChange = gridOption?.sortingChange || false;

  const tableBodyRef = useRef(null);
  const APPEND_ROWS = 45;
  const startIndex = 0;

  const [columnFilters, setColumnFilters] = useState({});
  const [columnTypeFilters, setColumnTypeFilters] = useState({});
  const [columns, setColumns] = useState([]);
  const [defaultPageSize, setDefaultPageSize] = useState(isPagination?.defaultPageSize || 10);
  const [openFilterColumnId, setOpenFilterColumnId] = useState(null);
  const [globalFilter, setGlobalFilter] = useState(undefined);

  const handleGlobalFilterChange = value => {
    setGlobalFilter(value);

    if (gridOption?.onGlobalFilterChange) {
      gridOption.onGlobalFilterChange(value);
    }
  };

  const handleFilteredRowsChange = rows => {
    if (gridOption?.onFilteredRowsChange) {
      gridOption.onFilteredRowsChange(rows);
    }
  };

  const [endIndex, setEndIndex] = useState(APPEND_ROWS); // Adjust this based on the number of visible rows

  const toggleFilterPopup = columnId => {
    setOpenFilterColumnId(prevColumnId => (prevColumnId === columnId ? null : columnId));
  };

  useEffect(() => {
    if (isLoaded) {
      setColumnFilters({});
      setColumnTypeFilters({});
      setColumns([]);
      setOpenFilterColumnId(null);
      setDefaultPageSize(isPagination?.defaultPageSize || 10);
    }
  }, [isLoaded]);

  // useEffect(() => {
  //   setColumnFilters({});
  // }, [gridOptionId]);

  useEffect(() => {
    if (!isLoaded) {
      const col = columnDefs?.map(x => {
        return { ...x, gridOptionId: gridOptionId };
      });
      const newColumns = col?.filter(x => !x?.isHiddenCol);
      const areEqual2 = _isEqual(columns, newColumns);
      if (!areEqual2) {
        const modifiedColumns = newColumns?.map(x => ({
          sortingFn: 'custom',
          ...x,
          header: !x.Header ? x.accessor : x.Header,
          accessorKey: x.accessor,
          size: x.width,
          minSize: x.minWidth,
          maxSize: x.maxWidth
        }));
        setColumns(modifiedColumns);
      }
    }
  }, [columnDefs, gridOptionId, isLoaded]);

  useEffect(() => {
    const defaultFilterOptions =
      (columnDefs?.find(x => x.filterSelectedByDefaultOption) || {}).filterSelectedByDefaultOption || [];

    let output = {};
    defaultFilterOptions?.forEach(option => {
      output[option.fieldName] = {
        label: option.label,
        value: option.value
      };
    });

    setColumnFilters(prevFilters => {
      const filtersArray = Array.isArray(prevFilters) ? prevFilters : [];
      return { ...filtersArray, ...output };
    });
  }, []);

  const sortTypeObj = useMemo(() => {
    const sortTypeList = columns?.filter(x => x?.sortBy) ?? [];
    let sortByArray = [];
    if (sortTypeList?.length > 0) {
      sortByArray = sortTypeList?.map(y => {
        if (y?.sortBy === DESCENDING) {
          return { id: y?.accessor, desc: true };
        } else {
          return { id: y?.accessor, desc: false };
        }
      });
    }
    return sortByArray;
  }, [columns]);

  const filteredData = useMemo(() => {
    let newfilteredData = gridRowData;
    const isAppliedColumnFilter = Object.keys(columnFilters)?.length;
    if (isAppliedColumnFilter > 0) {
      Object.entries(columnFilters)?.forEach(([columnId, filterValue]) => {
        const column = columns?.find(col => col.accessor === columnId);
        if (column && column?.filterType) {
          const filterType = column?.filterType;
          switch (filterType) {
            case 'text':
              newfilteredData = textFilterDataFn(newfilteredData, columnId, filterValue);
              break;
            case 'numberfilter':
              newfilteredData = numberFilterDataFn(newfilteredData, columnId, filterValue);
              break;
            case 'dropdown':
              newfilteredData = dropdownFilterDataFn(newfilteredData, columnId, filterValue);
              break;
            case 'datepicker':
              newfilteredData = dateFilterDataFn(newfilteredData, columnId, filterValue);
              break;
            default:
              break;
          }
        }
      });
    }
    if (
      newfilteredData &&
      newfilteredData?.length > 0 &&
      !isPagination?.status &&
      columns &&
      columns?.length > 0
    ) {
      setDefaultPageSize(newfilteredData.length);
    }
    return newfilteredData;
  }, [gridRowData, columns, columnFilters]);

  const downloadExcelFn = async excelFilterData => {
    const jsonData = isDownloadXls?.excelData || excelFilterData;
    if (jsonData.length > 0) {
      await downloadExcelByJsonFn({
        jsonData,
        fileName: isDownloadXls?.filename,
        replaceSelectedColumnNames: isDownloadXls?.replaceSelectedColumnNames,
        xlsSheetName: isDownloadXls?.xlsSheetName
      });
    }
  };

  if (isLoaded || (columns && columns?.length === 0)) {
    return (
      <CustomCard
        isShowCard={props.isShowCard}
        addedClass="p-3 mb-3"
        addedCardBodyClass="p-3"
        title={props.cardTitle}
        isCardLoading={isLoaded}
      />
    );
  }

  return (
    <ReactTable
      {...props}
      columnFilters={columnFilters && Object.keys(columnFilters).length > 0 ? columnFilters : []}
      setColumnFilters={setColumnFilters}
      columnTypeFilters={
        columnTypeFilters && Object.keys(columnTypeFilters).length > 0 ? columnTypeFilters : []
      }
      setColumnTypeFilters={setColumnTypeFilters}
      modifiedColumns={columns}
      setColumns={setColumns}
      defaultPageSize={defaultPageSize}
      setDefaultPageSize={setDefaultPageSize}
      sortTypeObj={sortTypeObj}
      downloadExcelFn={downloadExcelFn}
      filteredData={filteredData}
      columnDefs={columnDefs}
      sortingChange={sortingChange}
      openFilterColumnId={openFilterColumnId}
      setOpenFilterColumnId={setOpenFilterColumnId}
      toggleFilterPopup={toggleFilterPopup}
      tableBodyRef={tableBodyRef}
      startIndex={startIndex}
      endIndex={endIndex}
      setEndIndex={setEndIndex}
      globalFilter={globalFilter}
      setGlobalFilterValue={handleGlobalFilterChange}
      onFilteredRowsChange={handleFilteredRowsChange}
      APPEND_ROWS={APPEND_ROWS}
      gridOptionId={gridOptionId}
      hideHeader={hideHeader}
      cardClassName={cardClassName}
      isShowRowLoadingIcon={isShowRowLoadingIcon}
    />
  );
};

ReactTableContainer.displayName = 'ReactTableContainer';
export default memo(ReactTableContainer);
